import { isProductionBuilding } from "./buildings";
import { horizontalFacingToward } from "./fixtures";
import { coordEq, coordKey, getTile, inBounds, neighbors } from "./grid";
import { terrainEnterableByUnit } from "./terrain";
import type { Coord, FacingDir, GameState, PlayerId, UnitKind } from "./types";
import { isAirUnit } from "./units";

function defaultHorizontalFacing(owner: PlayerId): FacingDir {
  return owner === "p1" ? "right" : "left";
}

function enemyPlayer(owner: PlayerId): PlayerId {
  return owner === "p1" ? "p2" : "p1";
}

function enemyProductionPositions({
  state,
  owner,
}: {
  state: GameState;
  owner: PlayerId;
}): Coord[] {
  const enemy = enemyPlayer(owner);
  const positions: Coord[] = [];
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const tile = state.tiles[y * state.width + x]!;
      if (!isProductionBuilding(tile.kind) || tile.owner !== enemy) continue;
      positions.push({ x, y });
    }
  }
  return positions;
}

function closestByBirdDistance({
  from,
  candidates,
}: {
  from: Coord;
  candidates: Coord[];
}): Coord | null {
  let bestDist = Infinity;
  let bestPos: Coord | null = null;
  for (const candidate of candidates) {
    const dist = Math.abs(candidate.x - from.x) + Math.abs(candidate.y - from.y);
    if (dist < bestDist) {
      bestDist = dist;
      bestPos = candidate;
    }
  }
  return bestPos;
}

function facingFromFirstHorizontalStep({
  path,
  startPos,
  fallback,
}: {
  path: Coord[];
  startPos: Coord;
  fallback: FacingDir;
}): FacingDir {
  let prev = startPos;
  for (const step of path) {
    const dx = step.x - prev.x;
    if (dx > 0) return "right";
    if (dx < 0) return "left";
    prev = step;
  }
  return fallback;
}

function reconstructPath({
  prev,
  start,
  dest,
}: {
  prev: Map<string, Coord>;
  start: Coord;
  dest: Coord;
}): Coord[] {
  const path: Coord[] = [];
  let cur = dest;
  while (!coordEq(cur, start)) {
    path.unshift(cur);
    const parent = prev.get(coordKey(cur));
    if (!parent) return [];
    cur = parent;
  }
  return path;
}

function closestReachableByPath({
  state,
  unitKind,
  from,
  targets,
}: {
  state: GameState;
  unitKind: UnitKind;
  from: Coord;
  targets: Coord[];
}): { target: Coord; path: Coord[] } | null {
  if (targets.length === 0) return null;

  const targetKeys = new Set(targets.map(coordKey));
  const visited = new Set<string>([coordKey(from)]);
  const prev = new Map<string, Coord>();
  const queue: Coord[] = [from];

  while (queue.length > 0) {
    const pos = queue.shift()!;
    if (targetKeys.has(coordKey(pos)) && !coordEq(pos, from)) {
      return { target: pos, path: reconstructPath({ prev, start: from, dest: pos }) };
    }
    for (const n of neighbors(pos)) {
      if (!inBounds(state, n)) continue;
      const key = coordKey(n);
      if (visited.has(key)) continue;
      const tile = getTile(state, n);
      if (!terrainEnterableByUnit({ unitKind, tileKind: tile.kind })) continue;
      visited.add(key);
      prev.set(key, pos);
      queue.push(n);
    }
  }

  return null;
}

export function facingTowardClosestEnemyProduction({
  state,
  owner,
  pos,
  unitKind,
}: {
  state: GameState;
  owner: PlayerId;
  pos: Coord;
  unitKind: UnitKind;
}): FacingDir {
  const fallback = defaultHorizontalFacing(owner);
  const productions = enemyProductionPositions({ state, owner });
  if (productions.length === 0) return fallback;

  if (!isAirUnit(unitKind)) {
    const reachable = closestReachableByPath({
      state,
      unitKind,
      from: pos,
      targets: productions,
    });
    if (reachable) {
      return facingFromFirstHorizontalStep({
        path: reachable.path,
        startPos: pos,
        fallback,
      });
    }
  }

  const bestPos = closestByBirdDistance({ from: pos, candidates: productions });
  if (!bestPos) return fallback;
  return horizontalFacingToward({ from: pos, to: bestPos, fallback });
}
